import { pickTitle } from "@/api/content";
import type { Program, ProgramItem } from "@/api/program";
import { useLang } from "@/i18n/LanguageContext";
import { scheduleLabel } from "@/features/journeys/schedule";

/** "Now / Up next / Overdue" focus block above the full program list. */
export function ProgramFocus({ program }: { program: Program }) {
  const { t, lang } = useLang();
  const today = new Date().toISOString().slice(0, 10);

  const incomplete = program.items.filter((i) => !i.completed);
  const isAvailable = (i: ProgramItem) => !i.available_on || i.available_on <= today;
  const overdue = incomplete.filter((i) => i.required && i.available_on && i.available_on <= today);
  const now = incomplete.filter(isAvailable);
  const upcoming = incomplete
    .filter((i) => i.available_on && i.available_on > today)
    .sort((a, b) => (a.available_on! < b.available_on! ? -1 : 1));

  // The single most important thing to do next.
  const next = overdue[0] ?? now[0] ?? upcoming[0] ?? null;

  if (incomplete.length === 0) {
    return (
      <div className="focus focus--done">
        <strong>{t("Alles afgerond 🎉", "All done 🎉")}</strong>
        <span>{t("Je hebt je hele onboarding voltooid.", "You've completed your entire onboarding.")}</span>
      </div>
    );
  }

  return (
    <div className="focus">
      <div className="focus-stats">
        <span className={`focus-stat${overdue.length > 0 ? " focus-stat--err" : ""}`}>
          <b>{overdue.length}</b> {t("te laat", "overdue")}
        </span>
        <span className="focus-stat">
          <b>{now.length}</b> {t("nu te doen", "to do now")}
        </span>
        <span className="focus-stat">
          <b>{upcoming.length}</b> {t("komt eraan", "upcoming")}
        </span>
      </div>
      {next && (
        <p className="focus-next">
          <span className="focus-next-label">{t("Volgende", "Next")}</span>
          <strong>{pickTitle(next.title_i18n, lang, next.title)}</strong>
          <span className="focus-next-when">
            {next === upcoming[0] && next.available_on
              ? `${t("vanaf", "from")} ${new Date(next.available_on).toLocaleDateString()}`
              : scheduleLabel(next.offset_days, t)}
          </span>
        </p>
      )}
    </div>
  );
}
